import React, { useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Brain, Clock, Mail, MessageSquare, FileText, StickyNote, Activity, CheckCircle2, XCircle, Loader2, RefreshCw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import agentCasesService, { type AgentCaseDetail, type AgentCaseEvent } from '@/services/agent-cases.service';
import { formatDayTime } from '@/lib/format';
import AgentNeuralCanvas from '@/components/AgentNeuralCanvas';

// Beschriftungen bleiben als deutscher Quelltext im Code (defaultValue) und
// werden erst beim Rendern übersetzt — auf Modulebene darf t() nicht laufen.
const STATUS_META: Record<string, { key: string; label: string; cls: string }> = {
  active: { key: 'agent:caseStatus.active', label: 'Aktiv', cls: 'bg-blue-50 text-blue-600' },
  waiting_reply: { key: 'agent:caseStatus.waiting_reply', label: 'Wartet auf Antwort', cls: 'bg-amber-50 text-amber-600' },
  waiting_time: { key: 'agent:caseStatus.waiting_time', label: 'Wiedervorlage', cls: 'bg-violet-50 text-violet-600' },
  ready: { key: 'agent:caseStatus.ready', label: 'Bereit', cls: 'bg-emerald-50 text-emerald-600' },
  done: { key: 'agent:caseStatus.done', label: 'Abgeschlossen', cls: 'bg-slate-100 text-slate-500' },
  failed: { key: 'agent:caseStatus.failed', label: 'Fehlgeschlagen', cls: 'bg-rose-50 text-rose-600' },
};

const EVENT_META: Record<string, { key: string; label: string; icon: React.ElementType }> = {
  status: { key: 'agent:event.status', label: 'Status', icon: Activity },
  thought: { key: 'agent:event.thought', label: 'Gedanke', icon: Brain },
  action: { key: 'agent:event.action', label: 'Aktion', icon: Clock },
  internal_comm: { key: 'agent:event.internal_comm', label: 'Interne Kommunikation', icon: Mail },
  reply_received: { key: 'agent:event.reply_received', label: 'Antwort erhalten', icon: MessageSquare },
  draft: { key: 'agent:event.draft', label: 'Kundenentwurf', icon: FileText },
  note: { key: 'agent:event.note', label: 'Notiz', icon: StickyNote },
};

const fmt = (d?: string) => {
  if (!d) return '';
  const t = new Date(d);
  return isNaN(t.getTime()) ? '' : formatDayTime(t);
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const { t } = useTranslation();
  const m = STATUS_META[status];
  const label = m ? t(m.key, m.label) : status;
  const cls = m?.cls || 'bg-slate-100 text-slate-500';
  return (
    <span className={cn('agent-case-status', `agent-case-status--${status}`, cls)}>
      <span aria-hidden="true" className="agent-case-status-dot" />
      {label}
    </span>
  );
};

const TimelineItem: React.FC<{ ev: AgentCaseEvent }> = ({ ev }) => {
  const { t } = useTranslation();
  const m = EVENT_META[ev.kind];
  const label = m ? t(m.key, m.label) : ev.kind;
  const Icon = m?.icon || Activity;
  const meta = ev.metadata as Record<string, any> | null;
  return (
    <article className={cn('cloud-agent-event', `cloud-agent-event--${ev.kind}`)}>
      <div className="cloud-agent-event-icon" aria-hidden="true">
        <Icon />
      </div>
      <div className="cloud-agent-event-content">
        <div className="cloud-agent-event-heading">
          <span className="cloud-agent-event-label">{label}</span>
          <span className="cloud-agent-event-time">{fmt(ev.created_at)}</span>
          {meta?.child_ticket_number && (
            <span className="cloud-agent-event-reference">{String(meta.child_ticket_number)}</span>
          )}
        </div>
        {ev.content && <p className="cloud-agent-event-copy">{ev.content}</p>}
      </div>
    </article>
  );
};

/**
 * Kompakte Zeitleiste — wird IM Ticket (Seitenbereich) verwendet, wo für eine
 * Karte kein Platz ist. Die Vollansicht unter /agent-faelle zeigt alle Fälle
 * als Netz (AgentNeuralCanvas).
 */
export const CaseDetail: React.FC<{ ticketId: string }> = ({ ticketId }) => {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['agent-case', ticketId],
    queryFn: () => agentCasesService.getByTicket(ticketId),
    refetchInterval: 15000,
  });

  if (isLoading) return <div className="cloud-agent-state"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (!data) return <div className="cloud-agent-state">{t('common:x3.noCaseFound', 'Kein Fall gefunden')}</div>;

  const c = data.case;
  return (
    <div className="cloud-agent-detail">
      <header className="cloud-agent-overview">
        <div className="cloud-agent-overview-meta">
          <span className="cloud-agent-ticket-number">{c.ticket_number}</span>
          <StatusBadge status={c.status} />
        </div>
        <h3 className="cloud-agent-title">{c.subject}</h3>
        {c.summary && <p className="cloud-agent-summary">{c.summary}</p>}
      </header>
      <section className="cloud-agent-activity">
        <div className="cloud-agent-section-heading">
          <span aria-hidden="true" className="cloud-agent-section-accent" />
          <span>{t('agent:drafts.conversation', 'Verlauf')}</span>
          <span className="cloud-agent-event-count">{data.events.length}</span>
          <span aria-hidden="true" className="cloud-agent-section-rule" />
        </div>
        {data.events.length === 0 ? (
          <div className="cloud-agent-empty">{t('agent:drafts.noEvents', 'Noch keine Ereignisse')}</div>
        ) : (
          <div className="cloud-agent-event-list">{data.events.map((ev, i) => <TimelineItem key={i} ev={ev} />)}</div>
        )}
      </section>
    </div>
  );
};

const AgentCasesView: React.FC = () => {
  const { t } = useTranslation();
  // Details werden pro Fall nachgeladen: die Liste liefert nur Kopfdaten.
  // Aktive Fälle öffnen sich von selbst, abgeschlossene erst per Klick.
  const [expanded, setExpanded] = useState<string[]>([]);

  const { data: cases = [], isLoading } = useQuery({
    queryKey: ['agent-cases'],
    queryFn: () => agentCasesService.list(),
    refetchInterval: 15000,
  });

  const autoOpen = useMemo(
    () => cases.filter((c) => !['done', 'failed'].includes(c.status)).slice(0, 6).map((c) => c.ticket_id),
    [cases],
  );
  const openIds = useMemo(
    () => [...new Set([...autoOpen, ...expanded])].filter((id) => cases.some((c) => c.ticket_id === id)),
    [autoOpen, expanded, cases],
  );

  const detailQueries = useQueries({
    queries: openIds.map((ticketId) => ({
      queryKey: ['agent-case', ticketId],
      queryFn: () => agentCasesService.getByTicket(ticketId),
      refetchInterval: (query: { state: { data?: AgentCaseDetail | null } }) => {
        const status = query.state.data?.case?.status;
        return status && ['done', 'failed'].includes(status) ? false : 8000;
      },
    })),
  });

  const details = useMemo(() => {
    const map: Record<string, AgentCaseDetail | undefined> = {};
    openIds.forEach((id, i) => {
      const d = detailQueries[i]?.data;
      if (d) map[id] = d;
    });
    return map;
  }, [openIds, detailQueries]);

  const loadingCaseIds = openIds.filter((_, i) => detailQueries[i]?.isLoading);

  if (isLoading) {
    return (
      <div className="agent-map-state">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (!cases.length) {
    return (
      <div className="agent-map-state">
        <Brain className="h-7 w-7" />
        <p>{t('common:x3.noCasesStartAgent', 'Noch keine Fälle. Starte den Cloud-Agent.')}</p>
      </div>
    );
  }

  return (
    <AgentNeuralCanvas
      cases={cases as never}
      details={details as never}
      loadingCaseIds={loadingCaseIds}
      onExpand={(ticketId) => setExpanded((prev) => (prev.includes(ticketId) ? prev : [...prev, ticketId]))}
    />
  );
};

export default AgentCasesView;
